import type { DriverStop, StopKind } from "../lib/normalize";

/** #628: 未入力のまま退勤しようとしたときに本人へ出す説明。金額が出ない理由まで伝える。 */
export const MISSING_ODOMETER_MESSAGE =
  "到着オドメーターが未入力の配送があります。未入力のままだと、その配送の委託先支払距離を算出できません。当日中なら記憶で補えるため、ここで入力してください。";

const KIND_LABEL: Partial<Record<StopKind, string>> = {
  pickup: "集荷",
  collect: "回収",
  delivery: "配達",
};

/**
 * #628: 退勤操作時に「到着オドメーター未入力」の立寄り先を本人へ提示するダイアログ。
 *
 * 退勤自体はブロックしない (「あとで」で続行できる)。その場入力は打刻状態を変えない
 * オドメーターのみの更新 (App の editStopOdometer) を通す。
 * 全件埋まったら一覧は空になり、そのまま退勤へ進める。
 */
export function MissingOdometerDialog({
  stops,
  acting = false,
  error = null,
  onEnter,
  onProceed,
  onCancel,
}: {
  /** 未入力の立寄り先。空配列なら「未入力はありません」を表示し退勤へ進める */
  stops: DriverStop[];
  /** オドメーター更新 API 実行中。行の入力ボタンを無効化する */
  acting?: boolean;
  /** 直前の更新で発生したエラー (退勤自体は止めない) */
  error?: string | null;
  /** 行を選んでその場でオドメーターを入力する */
  onEnter: (stop: DriverStop) => void;
  /** 「あとで」/「退勤へ進む」。退勤打刻の確認へ続行する */
  onProceed: () => void;
  /** 退勤操作自体をやめて一覧へ戻る */
  onCancel: () => void;
}) {
  const remaining = stops.length;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="missing-odo-title"
      aria-describedby="missing-odo-description"
      className="safe-bottom fixed inset-0 z-[150] flex items-end justify-center bg-black/40 px-4 pt-4"
    >
      <div className="max-h-[90dvh] w-full max-w-[420px] overflow-y-auto rounded-2xl bg-[var(--bg-raised)] p-5 shadow-[0_-10px_30px_rgba(0,0,0,0.2)]">
        <h2 id="missing-odo-title" className="mb-1.5 text-[length:var(--fs-lg)] font-semibold">
          {remaining > 0 ? `到着オドメーター未入力 ${remaining}件` : "到着オドメーターは入力済み"}
        </h2>
        <p
          id="missing-odo-description"
          className="mb-3.5 text-[length:var(--fs-xs)] leading-relaxed text-[var(--ink-3)]"
        >
          {remaining > 0
            ? MISSING_ODOMETER_MESSAGE
            : "未入力の配送はありません。このまま退勤打刻へ進めます。"}
        </p>
        {error && (
          <p
            role="alert"
            className="mb-3.5 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]"
          >
            {error}
          </p>
        )}
        {remaining > 0 && (
          <ul className="mb-3.5 grid gap-2">
            {stops.map((stop) => (
              <li key={stop.id}>
                <button
                  type="button"
                  onClick={() => onEnter(stop)}
                  disabled={acting}
                  className="flex min-h-11 w-full items-center gap-2.5 rounded-lg border border-[var(--rule-soft)] bg-[var(--bg)] px-3 py-2.5 text-left text-[length:var(--fs-sm)] disabled:opacity-50"
                >
                  <span className="shrink-0 rounded bg-[var(--ink-4)] px-1.5 py-0.5 text-[length:var(--fs-xs)] text-white">
                    {KIND_LABEL[stop.kind] ?? "立寄"}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium text-[var(--ink-1)]">
                    {stop.name}
                  </span>
                  <span className="shrink-0 font-semibold text-[var(--info)]">入力</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="h-12 flex-1 cursor-pointer rounded-lg border border-[var(--rule-soft)] bg-transparent text-[length:var(--fs-sm)] font-semibold text-[var(--ink-2)]"
          >
            やめる
          </button>
          <button
            type="button"
            onClick={onProceed}
            className="h-12 flex-[2] cursor-pointer rounded-lg bg-[var(--accent)] text-[length:var(--fs-sm)] font-bold text-white"
          >
            {remaining > 0 ? "あとで" : "退勤へ進む"}
          </button>
        </div>
      </div>
    </div>
  );
}
